import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import PhotosAdmin from './admin/PhotosAdmin';
import CaseStudiesAdmin from './admin/CaseStudiesAdmin';
import AboutAdmin from './admin/AboutAdmin';

function BlankNote() {
  return { title: '', subtitle: 'Eileen Tadeo · Berlin', body: '', location: 'Berlin', published: true };
}

export default function Admin() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null); // note object or null
  const [busy, setBusy] = useState(false);
  const [statsBusy, setStatsBusy] = useState(false);

  useEffect(() => {
    if (ready && !user) nav('/admin/login');
  }, [ready, user, nav]);

  const refresh = async () => {
    const [n, s, m] = await Promise.all([
      api.get('/field-notes?include_drafts=true'),
      api.get('/dashboard/stats'),
      api.get('/messages'),
    ]);
    setNotes(n.data);
    setStats(s.data);
    setMessages(m.data);
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { title: editing.title, subtitle: editing.subtitle, body: editing.body, location: editing.location, published: editing.published };
      if (editing.id) await api.put(`/field-notes/${editing.id}`, payload);
      else await api.post('/field-notes', payload);
      toast.success('Saved');
      setEditing(null);
      refresh();
    } catch (e) { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    await api.delete(`/field-notes/${id}`);
    toast.success('Deleted');
    refresh();
  };

  const saveStats = async () => {
    setStatsBusy(true);
    try {
      await api.put('/dashboard/stats', {
        applications_tracked: Number(stats.applications_tracked),
        roles_scored: Number(stats.roles_scored),
        top_matcher_pct: Number(stats.top_matcher_pct),
        briefs_sent: Number(stats.briefs_sent),
        interviews_landed: Number(stats.interviews_landed),
        weeks_running: Number(stats.weeks_running),
      });
      toast.success('Dashboard updated');
    } catch { toast.error('Update failed'); }
    finally { setStatsBusy(false); }
  };

  if (!ready) return null;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-paper grain" data-testid="admin-page">
      <div className="border-b border-ink/10">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="mono text-[11px] uppercase tracking-[0.3em] text-ink/70 hover:text-orchid">← View site</Link>
          <div className="mono text-xs text-ink/60">{user.email}</div>
          <button onClick={() => { logout(); nav('/admin/login'); }} data-testid="logout-button" className="mono text-[11px] uppercase tracking-[0.3em] text-ink/70 hover:text-orchid">Log out</button>
        </div>
      </div>

      <main className="mx-auto max-w-[1200px] px-6 md:px-12 py-12">
        <h1 className="serif text-5xl md:text-6xl text-ink mb-2">The <span className="italic text-grape">back office</span>.</h1>
        <p className="mono text-sm text-ink/60 mb-12">Write field notes. Update the live pipeline. Close the door on the way out.</p>

        {/* Field Notes CMS */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="serif text-3xl text-ink">Field notes</h2>
            <button
              data-testid="new-note-button"
              onClick={() => setEditing(BlankNote())}
              className="bg-void text-paper px-5 py-3 mono uppercase tracking-[0.22em] text-[11px] hover:bg-orchid transition-colors"
            >
              + New entry
            </button>
          </div>

          <div className="border border-ink/10">
            {notes.map((n) => (
              <div key={n.id} className="grid grid-cols-12 gap-4 items-center border-b border-ink/10 last:border-0 p-5" data-testid={`admin-note-row-${n.number}`}>
                <div className="col-span-1 serif text-2xl text-orchid">{String(n.number).padStart(3, '0')}</div>
                <div className="col-span-8">
                  <div className="serif text-lg text-ink leading-snug">{n.title}</div>
                  <div className="mono text-[11px] text-ink/50 mt-1">{n.published ? 'Published' : 'Draft'} · {new Date(n.created_at).toLocaleDateString()}</div>
                </div>
                <div className="col-span-3 flex gap-2 justify-end">
                  <button onClick={() => setEditing(n)} className="mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-ink/20 hover:border-orchid">Edit</button>
                  <button onClick={() => remove(n.id)} className="mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-ink/20 hover:border-red-700">Delete</button>
                </div>
              </div>
            ))}
            {notes.length === 0 && <div className="p-8 mono text-sm text-ink/50">No entries yet.</div>}
          </div>
        </section>

        {/* Dashboard stats */}
        {stats && (
          <section className="mb-24">
            <h2 className="serif text-3xl text-ink mb-6">Live pipeline · numbers</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                ['top_matcher_pct', 'Top matcher %'],
                ['roles_scored', 'Roles scored'],
                ['applications_tracked', 'Applications tracked'],
                ['briefs_sent', 'Briefs sent'],
                ['interviews_landed', 'Interviews landed'],
                ['weeks_running', 'Weeks running'],
              ].map(([k, label]) => (
                <div key={k} className="border border-ink/15 p-4">
                  <div className="mono text-[10px] uppercase tracking-[0.25em] text-orchid mb-2">{label}</div>
                  <input
                    data-testid={`stat-input-${k}`}
                    type="number" value={stats[k]}
                    onChange={(e) => setStats({ ...stats, [k]: e.target.value })}
                    className="w-full bg-transparent serif text-3xl text-ink outline-none"
                  />
                </div>
              ))}
            </div>
            <button
              onClick={saveStats}
              disabled={statsBusy}
              data-testid="save-stats-button"
              className="mt-6 bg-void text-paper px-6 py-3 mono uppercase tracking-[0.22em] text-[11px] hover:bg-orchid transition-colors disabled:opacity-60"
            >
              {statsBusy ? 'Saving…' : 'Update dashboard'}
            </button>
          </section>
        )}

        {/* Messages inbox */}
        <section className="mb-16">
          <AboutAdmin />
        </section>

        <section className="mb-16">
          <PhotosAdmin />
        </section>

        <section className="mb-16">
          <CaseStudiesAdmin />
        </section>

        <section className="mb-24">
          <div className="flex items-center justify-between mb-6">
            <h2 className="serif text-3xl text-ink">Inbox <span className="mono text-sm text-ink/50 ml-3">{messages.filter(m => !m.read).length} unread · {messages.length} total</span></h2>
          </div>
          <div className="border border-ink/10">
            {messages.map((m) => (
              <div key={m.id} className={`p-5 border-b border-ink/10 last:border-0 ${m.read ? 'bg-paper' : 'bg-lilac/20'}`} data-testid={`admin-message-${m.id}`}>
                <div className="flex items-baseline justify-between gap-4 mb-2">
                  <div className="flex items-baseline gap-3 min-w-0">
                    <span className="serif text-lg text-ink truncate">{m.name}</span>
                    <a href={`mailto:${m.email}?subject=Re:${encodeURIComponent(m.subject || 'your note')}`} className="mono text-xs text-orchid hover:underline truncate">{m.email}</a>
                    {m.project && <span className="mono text-[10px] uppercase tracking-[0.2em] text-ink/50">· {m.project}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="mono text-[11px] text-ink/50">{new Date(m.created_at).toLocaleDateString()}</span>
                    {!m.read && (
                      <button
                        onClick={async () => { await api.patch(`/messages/${m.id}/read`); refresh(); }}
                        data-testid={`mark-read-${m.id}`}
                        className="mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-ink/20 hover:border-orchid"
                      >
                        Mark read
                      </button>
                    )}
                    <button
                      onClick={async () => { if (window.confirm('Delete message?')) { await api.delete(`/messages/${m.id}`); refresh(); } }}
                      data-testid={`delete-message-${m.id}`}
                      className="mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-ink/20 hover:border-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {m.subject && <div className="mono text-xs text-ink/60 mb-2">{m.subject}</div>}
                <p className="mono text-sm text-ink/80 leading-relaxed whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
            {messages.length === 0 && <div className="p-8 mono text-sm text-ink/50">No messages yet.</div>}
          </div>
        </section>

        {/* Editor modal */}
        {editing && (
          <div className="fixed inset-0 z-[100] bg-void/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto" data-testid="note-editor">
            <div className="w-full max-w-3xl bg-paper border border-ink/20 p-6 md:p-10">
              <div className="flex items-center justify-between mb-6">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid">{editing.id ? 'Edit entry' : 'New entry'}</div>
                <div className="flex items-center gap-3">
                  <label className="mono text-[10px] uppercase tracking-[0.22em] text-ink/70 cursor-pointer border border-ink/20 hover:border-orchid px-3 py-2">
                    Import .txt
                    <input
                      type="file"
                      accept=".txt,.md,text/plain,text/markdown"
                      data-testid="editor-import-file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          const text = String(ev.target?.result || '');
                          // Optional convention: first line "TITLE: My headline" fills the title,
                          // second line "SUBTITLE: ..." fills the byline. Everything else is the body.
                          const lines = text.split(/\r?\n/);
                          let title = editing.title;
                          let subtitle = editing.subtitle;
                          let bodyStart = 0;
                          const titleMatch = lines[0]?.match(/^\s*TITLE\s*:\s*(.+)$/i);
                          if (titleMatch) { title = titleMatch[1].trim(); bodyStart = 1; }
                          const subMatch = lines[bodyStart]?.match(/^\s*SUBTITLE\s*:\s*(.+)$/i);
                          if (subMatch) { subtitle = subMatch[1].trim(); bodyStart += 1; }
                          // strip a single blank line after headers
                          if (lines[bodyStart]?.trim() === '') bodyStart += 1;
                          const body = lines.slice(bodyStart).join('\n').trim();
                          setEditing({ ...editing, title, subtitle, body });
                          toast.success(`Loaded ${file.name}`);
                        };
                        reader.readAsText(file);
                        e.target.value = ''; // allow re-selecting the same file
                      }}
                    />
                  </label>
                  <button onClick={() => setEditing(null)} className="mono text-sm">✕</button>
                </div>
              </div>
              <p className="mono text-[11px] text-ink/50 mb-4">
                Tip: prep the entry in Notes / Word / VS Code as a plain <code>.txt</code>. Optional first two lines:{' '}
                <code>TITLE: your headline</code> and <code>SUBTITLE: Eileen Tadeo · Berlin</code>. Everything after that is the body. Blank lines separate paragraphs; lines starting with <code>— </code> render as bullets.
              </p>
              <div className="space-y-4">
                <input
                  data-testid="editor-title"
                  placeholder="Title" value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="w-full bg-transparent border-b border-ink/40 focus:border-orchid outline-none serif text-2xl md:text-3xl py-2"
                />
                <input
                  data-testid="editor-subtitle"
                  placeholder="Byline (e.g. Eileen Tadeo · Berlin)" value={editing.subtitle}
                  onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                  className="w-full bg-transparent border-b border-ink/40 focus:border-orchid outline-none mono text-sm py-2"
                />
                <textarea
                  data-testid="editor-body"
                  placeholder="Body (paragraphs separated by blank lines; prefix bullets with — )" value={editing.body}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  rows={16}
                  className="w-full bg-transparent border border-ink/20 focus:border-orchid outline-none mono text-sm p-4 leading-relaxed"
                />
                <label className="mono text-xs flex items-center gap-2">
                  <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="editor-published" />
                  Published (visible on public site)
                </label>
              </div>
              <div className="flex gap-3 justify-end mt-8">
                <button onClick={() => setEditing(null)} className="mono text-[11px] uppercase tracking-[0.22em] px-5 py-3 border border-ink/20 hover:border-orchid">Cancel</button>
                <button
                  onClick={save}
                  disabled={busy}
                  data-testid="editor-save"
                  className="bg-void text-paper px-6 py-3 mono uppercase tracking-[0.22em] text-[11px] hover:bg-orchid transition-colors disabled:opacity-60"
                >
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
